<template>
  <permission-gate
    moduleKey="propuestas"
    :showNoEditAlert="true"
  >
    <template #default="{ canEdit }">
      <div class="container">
        <div
          v-for="(cotizacion, index) in props.data"
          :key="cotizacion.CodigoCotizacion ?? index"
          class="cotizacion-item"
        >
          <el-table
            :data="cotizacion.items"
            style="width: 100%; margin-bottom: 40px"
            :border="true"
            :header-cell-style="headerStyle"
          >
            <el-table-column
              prop="CodigoCotizacion"
              label="Nro Cotizacion"
              width="77"
            >
              <template #header>
                <div class="text-header">Nro<br />Cotizacion</div>
              </template>
              <template #default="scope">
                <template v-if="scope.row?.CodigoCotizacion">
                  <router-link
                    :to="buildLink(scope.row.CodigoCotizacion)"
                    class="cot-link"
                  >
                    {{ scope.row.CodigoCotizacion }}
                  </router-link>
                </template>
                <template v-else>-</template>
              </template>
            </el-table-column>
            <el-table-column
              prop="Cantidad"
              label="Cantidad"
              width="75"
            />
            <el-table-column
              prop="Cotizacion.UnidadVenta.Descripcion"
              label="Unidad de venta"
              width="67"
            >
              <template #header>
                <div class="text-header">Unidad<br />de venta</div>
              </template>
            </el-table-column>
            <el-table-column
              prop="Cotizacion.Referencia"
              label="Descripcion"
            />
            <el-table-column
              prop="PrecioUnidadVenta"
              label="Precio en dólares x unidad de venta"
              width="90"
            >
              <template #default="scope">
                {{ $formatDecimal(scope.row.PrecioUnidadVenta, decimalesImportes) }}
              </template>
              <template #header>
                <div class="text-header">
                  {{ cotizacion.items[0]?.Cotizacion?.Moneda?.Simbolo ?? '' }} x
                  {{ cotizacion.items[0]?.Cotizacion?.UnidadVenta?.Descripcion ?? 'Unidad' }}
                </div>
              </template>
            </el-table-column>
            <el-table-column
              v-if="canEdit"
              width="130"
            >
              <template #default="scope">
                <el-row style="justify-content: space-between">
                  <el-popover
                    v-if="scope.row.Codigo"
                    placement="right"
                    :width="40"
                    effect="dark"
                    trigger="hover"
                    content="Eliminar cantidad"
                  >
                    <template #reference>
                      <el-button
                        :icon="IconDelete"
                        type="danger"
                        :disabled="!isConPropuesta(scope.row)"
                        @click.prevent="deleteRow(scope.row, index, scope.$index)"
                      >
                      </el-button>
                    </template>
                  </el-popover>
                  <div v-if="scope.row.Codigo">
                    <el-tooltip
                      v-if="scope.row.Aprobada === null"
                      :content="
                        !isConPropuesta(scope.row)
                          ? 'Debe crear la propuesta comercial primero'
                          : 'Aprobar cantidad'
                      "
                      placement="top"
                      effect="dark"
                    >
                      <span>
                        <el-button
                          type="primary"
                          :disabled="!isConPropuesta(scope.row)"
                          @click.prevent="approveRow(scope.row)"
                          >Aprobar</el-button
                        >
                      </span>
                    </el-tooltip>
                    <div
                      v-else-if="scope.row.Aprobada === true && scope.row.CodigoUsuarioAprobacion"
                      style="color: green"
                    >
                      Aprobada
                    </div>
                    <div
                      v-else
                      style="color: red"
                    >
                      Rechazada
                    </div>
                  </div>
                  <slot
                    name="custom-actions"
                    :row="scope.row"
                  ></slot>
                </el-row>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </template>
  </permission-gate>
</template>

<script setup>
import {
  ElMessage,
  ElMessageBox,
} from 'element-plus';
import { EstadoSolicitud } from '@/enum/estado_solicitud';
import IconDelete from '~icons/mdi/delete';
import PermissionGate from '@/shared/components/permission_gate.vue';
import { inject } from 'vue';
const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  withSelection: {
    type: Boolean,
    required: false,
  },
});
const emit = defineEmits(['deleteItem', 'approveItem', 'require-products']);

const decimalesImportes = inject('decimalesImportes', 2);

const headerStyle = () => {
  return { 'background-color': '#808080', color: '#ccc' };
};

const buildLink = (codigo) => {
  if (!codigo) return null;
  return `/page/prices/edit/${codigo}`;
};

const deleteRow = (row, indexCotizacion, indexCantidad) => {
  ElMessageBox.confirm(
    `¿Quiere eliminar la cantidad ${row.Cantidad} de la cotizacion ${row.CodigoCotizacion}?`,
    'Confirmación',
    {
      confirmButtonText: 'Si',
      cancelButtonText: 'No',
      type: 'warning',
    },
  )
    .then(() => {
      emit('deleteItem', { indexCotizacion, indexCantidad });
      ElMessage({
        type: 'success',
        message: 'Cantidad eliminada',
      });
    })
    .catch((err) => {
      console.log(err);
    });
};

const approveRow = (row) => {
  if (!row) return;

  const cotizacion = row.Cotizacion;
  const productos = cotizacion?.CotizacionProducto || [];

  const tieneProductosVacios = productos.length === 0;
  const faltanCodigos = productos.some((p) => !p.CodigoProducto);

  if (tieneProductosVacios || faltanCodigos) {
    emit('require-products', row);
    return;
  }
  continueApproval(row);
};

const continueApproval = (row) => {
  ElMessageBox.confirm(
    `¿Quiere aprobar la cantidad ${row.Cantidad} de la cotizacion ${row.CodigoCotizacion}?. Si confirma automáticamente se rechazarán el resto de las cantidades de dicha cotización si es que hay.`,
    'Confirmación',
    { confirmButtonText: 'Si', cancelButtonText: 'No', type: 'warning' },
  )
    .then(() => {
      checkAndPromptTechnicalSpec(row);
    })
    .catch((err) => {
      console.log(err);
    });
};

const checkAndPromptTechnicalSpec = (row) => {
  const productos = row.Cotizacion?.CotizacionProducto || [];
  const productosSinET = productos.filter(
    (p) => !p.CodigoEspecificacionTecnica || p.CodigoEspecificacionTecnica === 0,
  );
  const productosConET = productos.filter(
    (p) => p.CodigoEspecificacionTecnica && p.CodigoEspecificacionTecnica > 0,
  );
  const etUnicas = Array.from(new Set(productosConET.map((p) => p.CodigoEspecificacionTecnica)));

  if (etUnicas.length === 0) {
    emit('approveItem', {
      CodigoCotizacion: row.CodigoCotizacion,
      CodigoCotizacionCantidad: row.Codigo,
      forzarCreacionET: true,
    });
    return;
  }

  if (etUnicas.length > 1 || (etUnicas.length > 0 && productosSinET.length > 0)) {
    ElMessageBox.confirm(
      'Los productos de esta cotización provienen de diferentes Especificaciones Técnicas o hay productos nuevos sin molde. El sistema agrupará todos los artículos y creará una NUEVA Especificación Técnica unificada. ¿Desea continuar?',
      'Creación de ET Unificada',
      {
        confirmButtonText: 'Sí, crear ET unificada',
        cancelButtonText: 'Cancelar',
        type: 'warning',
      },
    )
      .then(() => {
        emit('approveItem', {
          CodigoCotizacion: row.CodigoCotizacion,
          CodigoCotizacionCantidad: row.Codigo,
          forzarCreacionET: true,
        });
      })
      .catch(() => {
        ElMessage({ type: 'info', message: 'Aprobación cancelada.' });
      });
    return;
  }

  const numeroET = etUnicas[0];
  ElMessageBox.confirm(
    `Todos los productos de esta cotización comparten la Especificación Técnica (N° ${numeroET}). ¿Desea generar OTRA nueva ET para estos productos, o prefiere reutilizar la existente para la Orden de Producción?`,
    'Especificación Técnica Existente',
    {
      confirmButtonText: 'Crear nueva ET',
      cancelButtonText: 'Reutilizar existente',
      type: 'info',
      distinguishCancelAndClose: true,
    },
  )
    .then(() => {
      emit('approveItem', {
        CodigoCotizacion: row.CodigoCotizacion,
        CodigoCotizacionCantidad: row.Codigo,
        forzarCreacionET: true,
      });
    })
    .catch((action) => {
      if (action === 'cancel') {
        emit('approveItem', {
          CodigoCotizacion: row.CodigoCotizacion,
          CodigoCotizacionCantidad: row.Codigo,
          forzarCreacionET: false,
        });
      } else {
        ElMessage({ type: 'info', message: 'Aprobación cancelada.' });
      }
    });
};

const isConPropuesta = (row) => {
  const cotizacion = row.Cotizacion;

  if (!cotizacion) return false;

  const estadoTexto = cotizacion.Estado ? cotizacion.Estado.trim().toLowerCase() : '';
  const descripcionSolicitud = cotizacion.EstadoSolicitud?.Descripcion
    ? cotizacion.EstadoSolicitud.Descripcion.trim().toLowerCase()
    : '';

  return (
    cotizacion.CodigoEstado === EstadoSolicitud.CON_PROPUESTA ||
    cotizacion.EstadoSolicitud?.Codigo === EstadoSolicitud.CON_PROPUESTA ||
    estadoTexto === 'con propuesta' ||
    descripcionSolicitud === 'con propuesta'
  );
};
defineExpose({ continueApproval });
</script>

<style scoped>
.container {
  width: 100%;
  display: flex;
  flex-direction: column;
}
.text-header {
  font-weight: 700;
}
.cot-link {
  color: blue;
  text-decoration: underline;
  cursor: pointer;
}
</style>
